/**
 * Lookup contracts — DESIGN.md §6.6, §6.7 (R10, R13).
 *
 * Statuses, tags, and custom-field definitions are *data* (§1.2), so these
 * schemas describe rows an admin edits rather than an enumeration the code
 * branches on. `category` is the one exception the code may read: reporting
 * rolls up on it precisely so a rename never breaks a metric (§6.6).
 */
import { z } from 'zod';
import { FIELD_TYPES, SCOPES, STATUS_CATEGORIES } from '../enums.js';
import { colorSchema, name, positionSchema, uuidSchema } from './common.js';
export const statusSchema = z.object({
    id: uuidSchema,
    name: z.string(),
    color: z.string(),
    category: z.enum(STATUS_CATEGORIES),
    position: z.string(),
    isDefault: z.boolean(),
    archivedAt: z.string().nullable(),
});
export const tagSchema = z.object({
    id: uuidSchema,
    name: z.string(),
    color: z.string(),
    archivedAt: z.string().nullable(),
});
export const fieldOptionSchema = z.object({
    id: uuidSchema,
    label: z.string(),
    color: z.string(),
    position: z.string(),
});
export const fieldDefinitionSchema = z.object({
    id: uuidSchema,
    name: z.string(),
    type: z.enum(FIELD_TYPES),
    options: z.array(fieldOptionSchema).nullable(),
    scope: z.enum(SCOPES),
    projectId: uuidSchema.nullable(),
    position: z.string(),
    archivedAt: z.string().nullable(),
});
/**
 * One call backs every picker in the app: the status dropdown, the tag
 * typeahead, the custom-field grid, and the MCP `list_lookups` tool (§12.5).
 * Cheap enough to cache client-side and invalidate on `lookup.changed`.
 */
export const lookupsSchema = z.object({
    statuses: z.array(statusSchema),
    tags: z.array(tagSchema),
    fields: z.array(fieldDefinitionSchema),
});
// ---------------------------------------------------------------- requests
export const createStatusRequestSchema = z
    .object({
    name: name(100),
    color: colorSchema,
    category: z.enum(STATUS_CATEGORIES),
    position: positionSchema.optional(),
    isDefault: z.boolean().default(false),
})
    .strict();
export const updateStatusRequestSchema = z
    .object({
    name: name(100).optional(),
    color: colorSchema.optional(),
    category: z.enum(STATUS_CATEGORIES).optional(),
    position: positionSchema.optional(),
    isDefault: z.literal(true).optional(),
})
    .strict();
/** §6.6 — archiving requires somewhere for the open cards to go. */
export const archiveStatusRequestSchema = z.object({ migrateToId: uuidSchema }).strict();
export const createTagRequestSchema = z.object({ name: name(100), color: colorSchema }).strict();
export const updateTagRequestSchema = z
    .object({ name: name(100).optional(), color: colorSchema.optional() })
    .strict();
/** Option ids are server-generated on create and never reused (§6.7). */
export const fieldOptionInputSchema = z
    .object({
    id: uuidSchema.optional(),
    label: name(200),
    color: colorSchema,
})
    .strict();
export const createFieldRequestSchema = z
    .object({
    name: name(100),
    type: z.enum(FIELD_TYPES),
    options: z.array(fieldOptionInputSchema).max(100).optional(),
    scope: z.enum(SCOPES).default('global'),
    projectId: uuidSchema.nullish(),
})
    .strict();
export const updateFieldRequestSchema = z
    .object({
    name: name(100).optional(),
    options: z.array(fieldOptionInputSchema).max(100).optional(),
    position: positionSchema.optional(),
})
    .strict();
//# sourceMappingURL=lookups.js.map